import React, {useEffect, useRef, useState} from 'react';

import {getMonth, getYear} from 'date-fns';
import i18n from 'i18next';
import {ReactDatePickerProps} from 'react-datepicker';
import {useTranslation} from 'react-i18next';

import 'react-datepicker/dist/react-datepicker.css';
import {ReactComponent as LeftArrowIcon} from 'assets/svg/arrow-left.svg';

import {yearsOptions} from 'utils/select';

import {Icon} from '../Icon';
import {Select, SelectOption} from '../Select';

import {DatePickerInput} from './input';
import {DatePickerArrow, DatePickerHead, InnerDatePicker} from './styled';

export type DatePickerProps = Omit<ReactDatePickerProps, 'onChange' | 'value'> & {
    onChange?: (date: Date | null, event: React.SyntheticEvent<any> | undefined) => void;
    value?: Date | null;
    label?: string;
    hasError?: boolean;
    formFieldName?: string;
    isRequired?: boolean;
    keepHeaderSelectOpenOnBlur?: boolean;
};

export const DatePicker = ({
    onChange = () => {},
    hasError,
    customInput,
    className,
    label,
    value,
    isRequired = false,
    keepHeaderSelectOpenOnBlur = false,
    ...other
}: DatePickerProps) => {
    const {t} = useTranslation();
    const [headerMenu, setHeaderMenu] = useState<'month' | 'year' | null>(null);
    const headerMenuOpenRef = useRef(false);
    const headerRef = useRef<HTMLDivElement>(null);
    const options = yearsOptions(
        other.minDate ? getYear(other.minDate) : 2020,
        other.maxDate ? getYear(other.maxDate) : new Date().getFullYear(),
    );
    const monthsKeys = [
        'january',
        'february',
        'march',
        'april',
        'may',
        'june',
        'july',
        'august',
        'september',
        'october',
        'november',
        'december',
    ];
    const months = monthsKeys.map((key, i) => ({
        label: t('dictionaries.months.' + key),
        value: i,
    }));

    const openHeaderMenu = (menu: 'month' | 'year') => {
        headerMenuOpenRef.current = true;
        setHeaderMenu(menu);
    };
    const closeHeaderMenu = () => {
        headerMenuOpenRef.current = false;
        setHeaderMenu(null);
    };
    const prepareHeaderMenuOpen = (event: React.MouseEvent<HTMLDivElement>) => {
        if (
            keepHeaderSelectOpenOnBlur &&
            (event.target as HTMLElement).closest('.react-select__control')
        ) {
            headerMenuOpenRef.current = true;
        }
    };

    useEffect(() => {
        if (headerMenu === null) return;

        const closeOnOutsideMouseDown = (event: MouseEvent) => {
            if (headerRef.current?.contains(event.target as Node)) return;

            headerMenuOpenRef.current = false;
            setHeaderMenu(null);
        };

        document.addEventListener('mousedown', closeOnOutsideMouseDown, true);
        return () => document.removeEventListener('mousedown', closeOnOutsideMouseDown, true);
    }, [headerMenu]);

    const renderCustomHeader = ({
        date,
        decreaseMonth,
        increaseMonth,
        changeMonth,
        changeYear,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
    }: Parameters<NonNullable<ReactDatePickerProps['renderCustomHeader']>>[0]) => (
        <DatePickerHead
            ref={headerRef}
            {...(keepHeaderSelectOpenOnBlur && {onMouseDownCapture: prepareHeaderMenuOpen})}
        >
            <DatePickerArrow
                type="button"
                onClick={decreaseMonth}
                disabled={prevMonthButtonDisabled}
            >
                <Icon source={LeftArrowIcon} height={14} width={14} />
            </DatePickerArrow>
            <Select
                options={months}
                value={months.find((o) => o.value === getMonth(date))}
                {...(keepHeaderSelectOpenOnBlur && {
                    menuIsOpen: headerMenu === 'month',
                    onMenuOpen: () => openHeaderMenu('month'),
                })}
                onChange={
                    keepHeaderSelectOpenOnBlur
                        ? (v: SelectOption<number>) => {
                              if (v.value != null) changeMonth(v.value);
                              closeHeaderMenu();
                          }
                        : (v: SelectOption<number>) => v.value && changeMonth(v.value)
                }
            />
            <Select
                options={options}
                value={options.find((o) => o.value === getYear(date))}
                {...(keepHeaderSelectOpenOnBlur && {
                    menuIsOpen: headerMenu === 'year',
                    onMenuOpen: () => openHeaderMenu('year'),
                })}
                onChange={
                    keepHeaderSelectOpenOnBlur
                        ? (v: SelectOption<number>) => {
                              if (v.value != null) changeYear(v.value);
                              closeHeaderMenu();
                          }
                        : (v: SelectOption<number>) => v.value && changeYear(v.value)
                }
            />
            <DatePickerArrow
                type="button"
                onClick={increaseMonth}
                disabled={nextMonthButtonDisabled}
                right
            >
                <Icon source={LeftArrowIcon} height={14} width={14} />
            </DatePickerArrow>
        </DatePickerHead>
    );

    return (
        <InnerDatePicker
            selected={value}
            renderCustomHeader={renderCustomHeader}
            onChange={onChange}
            popperClassName={className}
            popperPlacement="bottom-start"
            popperModifiers={{
                preventOverflow: {enabled: true, boundariesElement: 'viewport'},
                flip: {enabled: true},
            }}
            showPopperArrow={false}
            dateFormat="dd.MM.yyyy"
            autoComplete="off"
            locale={i18n.language}
            customInput={
                React.isValidElement(customInput) ? (
                    <customInput.type {...customInput.props} label={label} hasError={hasError} />
                ) : (
                    <DatePickerInput
                        isRequired={isRequired}
                        label={label}
                        hasError={hasError}
                        placeholder={t('components.datepicker.SelectDate')}
                    />
                )
            }
            {...other}
            {...(keepHeaderSelectOpenOnBlur && {
                onCalendarClose: () => {
                    if (headerMenuOpenRef.current) return;
                    closeHeaderMenu();
                    other.onCalendarClose?.();
                },
                ...(headerMenu !== null && other.open !== false && {open: true}),
            })}
        />
    );
};

export {globalStyles as globalDatePickerStyles} from './styled';
